import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Search, BookOpen, Layers, MessageSquare, Users, ArrowUpLeft, GitCompareArrows, Share2, Calendar, ArrowLeft, UserPlus, HelpCircle, ChevronDown, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useSystemSettings, getMediaType } from "@/hooks/use-system-settings";
import { useQuery } from "@tanstack/react-query";
import { SystemCard } from "@/components/systems/SystemCard";
import { APP_NAME } from "@/lib/constants";
import PartnersSection from "@/components/home/PartnersSection";
import { SEO } from "@/components/SEO";
import { PublisherPromoModal } from "@/components/home/PublisherPromoModal";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";

const fallbackFaqs = [
  { id: '1', q: `מה זה ${APP_NAME}?`, a: `${APP_NAME} היא פלטפורמה המאפשרת ליוצרים לפרסם מערכות ופתרונות דיגיטליים חכמים, ולמשתמשים לגלות אותם. אנחנו מחברים בין טכנולוגיה לאנשים.` },
  { id: '2', q: "האם פרסום מערכת עולה כסף?", a: `לא. פרסום מערכת ב-${APP_NAME} הוא בחינם לחלוטין. מטרתנו היא לתת במה ליוצרים.` },
  { id: '3', q: "למה המערכת שלי לא אושרה?", a: "כל מערכת עוברת בדיקה ידנית כדי לוודא שהיא עומדת בסטנדרטים שלנו. סיבות נפוצות לדחייה: מידע חסר, תיאור לא ברור, או תוכן שאינו הולם. תקבלו התראה עם הסיבה המדויקת." },
  { id: '4', q: "איך עובד הדירוג?", a: "כל משתמש רשום יכול לדרג מערכת פעם אחת. ניתן לערוך את הדירוג אחת ל-24 שעות. לא ניתן לדרג מערכת שיצרתם בעצמכם. מטרת הדירוגים היא ליצור תמונת מצב אמינה ושקופה." },
  { id: '5', q: "איך מוחקים חשבון?", a: "ניתן לפנות אלינו דרך טופס יצירת הקשר בנושא \"מחיקת חשבון\". המידע האישי שלכם יימחק תוך 30 יום עסקים בהתאם למדיניות הפרטיות." },
  { id: '6', q: "איך אני יכול לערוך את המערכת שפרסמתי?", a: "היכנסו ללוח הבקרה (ניהול מערכות), ולחצו על אייקון העריכה. שימו לב: עריכה מהותית עשויה להחזיר את המערכת לסטטוס \"ממתין לאישור\" לבדיקה חוזרת." },
];

const DEFAULT_ABOUT_CONTENT = `
<p><strong>Feevho</strong> היא פלטפורמה לפרסום וגילוי מערכות ואפליקציות.</p>
<p>הפלטפורמה נוצרה מתוך מציאות מוכרת:<br>אנשים בונים מערכות מצוינות — אבל אף אחד לא נחשף אליהן.<br>ובמקביל, אנשים מחפשים פתרונות — ומבזבזים זמן בלי לדעת איפה למצוא אותם.</p>
<p><strong>Feevho פותרת את שני הצדדים:</strong><br>בוני מערכות יכולים לפרסם בחינם ולקבל חשיפה אורגנית אמיתית לקהל רלוונטי,<br>ומשתמשים יכולים למצוא, להשוות ולבחור פתרונות במקום אחד — בקלות.</p>
`.trim();

const DEFAULT_SECTIONS_ORDER = [
  "partners", "about_us", "stats", "categories", "how_it_works", "featured", "articles", "faq", "cta"
];

const Index = () => {
  const [session, setSession] = useState<any>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const { data: settings, isLoading: isLoadingSettings } = useSystemSettings();
  
  const heroMedia = settings?.hero_media_url || "https://feevho.com/feevho-cover.mp4";
  const mediaType = getMediaType(heroMedia);

  const heroTitle = settings?.home_hero_title || "Feevho – הבית למערכות, אפליקציות וכלי AI";
  const heroDescription = settings?.home_hero_description || `${APP_NAME} היא הזירה הראשונה בישראל למערכות שנבנו עם AI. גלו פתרונות חכמים, השוו פיצ׳רים וקראו ביקורות אמיתיות. יצרתם מערכת? פרסמו אותה בפרופיל מקצועי והגיעו לקהל רחב.`;

  const topHeadingText = settings?.home_top_heading_text || "Feevho - פִיבוֹ";
  const topHeadingImage = settings?.home_top_heading_image || "";
  const showTopHeading = settings?.home_show_top_heading !== 'false';
  const topHeadingType = settings?.home_top_heading_type || 'text';

  const aboutTitle = settings?.home_about_title || "מי אנחנו";
  const aboutContent = settings?.home_about_content || DEFAULT_ABOUT_CONTENT;

  const showPartners = settings?.home_show_partners !== 'false';
  const showAboutUs = settings?.home_show_about_us !== 'false';
  const showStats = settings?.home_show_stats !== 'false';
  const showCategories = settings?.home_show_categories !== 'false';
  const showHowItWorks = settings?.home_show_how_it_works !== 'false';
  const showFeatured = settings?.home_show_featured !== 'false';
  const showArticles = settings?.home_show_articles !== 'false';
  const showFaq = settings?.home_show_faq !== 'false';
  const showCta = settings?.home_show_cta !== 'false';
  const showPromoModal = settings?.home_show_promo_modal !== 'false';

  const faqItems = settings?.home_faq_items ? JSON.parse(settings.home_faq_items) : fallbackFaqs;
  
  let sectionsOrder = DEFAULT_SECTIONS_ORDER;
  if (settings?.home_sections_order) {
    try {
      const parsed = JSON.parse(settings.home_sections_order);
      const missing = DEFAULT_SECTIONS_ORDER.filter(s => !parsed.includes(s));
      sectionsOrder = [...parsed, ...missing];
    } catch {
      // fallback to default
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const trackHomeView = async () => {
      const lastViewKey = 'home_view_tracked';
      if (sessionStorage.getItem(lastViewKey)) return;
      
      let anonId = localStorage.getItem('anon_session_id');
      if (!anonId) {
        anonId = crypto.randomUUID();
        localStorage.setItem('anon_session_id', anonId);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source') || (urlParams.has('fbclid') ? 'facebook' : null);
      const utmMedium = urlParams.get('utm_medium');
      const utmCampaign = urlParams.get('utm_campaign');

      const { data: { user } } = await supabase.auth.getUser();

      try {
        await supabase.from('page_views' as any).insert({
          path: '/',
          viewer_user_id: user?.id || null,
          anon_session_id: anonId,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign
        } as any);
      } catch (e) {
      }

      sessionStorage.setItem(lastViewKey, 'true');
    };
    
    trackHomeView();
  }, []);

  useEffect(() => {
    if (mediaType === 'video' && heroMedia && !isLoadingSettings) {
      const vid = heroVideoRef.current;
      if (vid) {
        vid.muted = true;
        vid.play().catch(() => {});
      }
    }
  }, [mediaType, heroMedia, isLoadingSettings]);

  const { data: stats } = useQuery({
    queryKey: ['home-stats'],
    queryFn: async () => {
      const [systemsRes, ratingsRes, categoriesRes] = await Promise.all([
        supabase.from('systems').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('ratings').select('*', { count: 'exact', head: true }),
        supabase.from('categories').select('*', { count: 'exact', head: true }).eq('is_active', true),
      ]);
      return { systems: systemsRes.count || 0, ratings: ratingsRes.count || 0, categories: categoriesRes.count || 0 };
    },
    enabled: showStats
  });

  const { data: featuredSystems } = useQuery({
    queryKey: ['featured-systems'],
    queryFn: async (): Promise<any[]> => {
      const { data } = await supabase
        .from('systems')
        .select('*, category:categories(name_he), system_images(public_url, is_cover), ratings(stars)')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(4);

      return (data || []).map((sys: any) => {
        const ratings = sys.ratings || [];
        const count = ratings.length;
        const avg = count > 0 ? ratings.reduce((acc: number, r: any) => acc + r.stars, 0) / count : 0;
        return { ...sys, rating_avg: avg, rating_count: count, system_images: sys.system_images };
      });
    },
    enabled: showFeatured
  });

  const { data: latestArticles } = useQuery({
    queryKey: ['home-latest-articles'],
    queryFn: async (): Promise<any[]> => {
      const { data } = await supabase.from('articles' as any).select('*').eq('is_published', true).order('created_at', { ascending: false }).limit(3);
      return data || [];
    },
    enabled: showArticles
  });

  const { data: categories } = useQuery({
    queryKey: ['home-categories'],
    queryFn: async (): Promise<any[]> => {
      const { data: cats } = await supabase.from('categories').select('*').eq('is_active', true);
      if (!cats || cats.length === 0) return [];
      
      const { data: activeSystems } = await supabase
        .from('systems')
        .select('category_id')
        .eq('status', 'approved');
        
      const activeCategoryIds = new Set((activeSystems || []).map((s: any) => s.category_id));
      return cats.filter((cat: any) => activeCategoryIds.has(cat.id));
    },
    enabled: showCategories
  });

  // Render Functions for Sections
  const renderPartners = () => showPartners && <PartnersSection key="partners" />;
  
  const renderAboutUs = () => showAboutUs && (
    <section key="about_us" className="py-24 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center space-y-10">
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">{aboutTitle}</h2>
        <div className="mx-auto text-lg lg:text-xl text-slate-600 leading-relaxed font-medium [&_.markdown-content>p]:mb-8 [&_.markdown-content>p:last-child]:mb-0 [&_.markdown-content_strong]:text-slate-900">
          <MarkdownRenderer content={aboutContent} allowStyle={true} />
        </div>
      </div>
    </section>
  );

  const renderStats = () => showStats && (
    <section key="stats" className="py-24 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto text-center">
          {[
            { value: stats?.systems || 0, label: "מערכות חכמות", icon: Layers },
            { value: stats?.ratings || 0, label: "חוות דעת", icon: MessageSquare },
            { value: stats?.categories || 0, label: "קטגוריות", icon: Users },
          ].map((stat, idx) => (
            <div key={idx}>
              <div className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">{stat.value > 0 ? stat.value : "—"}</div>
              <div className="text-sm text-slate-500 mt-1 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCategories = () => showCategories && categories && categories.length > 0 && (
    <section key="categories" className="py-32 overflow-hidden border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-14 md:mb-20">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight px-4">בכל תחום, לכל מטרה</h2>
          <p className="text-slate-500 mt-3 text-base max-w-lg mx-auto px-6">מצאו בדיוק את הפתרון שאתם צריכים, מניהול זמן ועד יצירה ופנאי.</p>
        </div>
      </div>

      <div className="relative overflow-hidden w-full">
        <div className="flex gap-3 md:gap-4 w-max hover:[animation-play-state:paused]" style={{ animation: 'marquee-rtl 120s linear infinite' }}>
          {[...(categories || []), ...(categories || []), ...(categories || []), ...(categories || [])].map((cat: any, i: number) => (
            <Link key={`${cat.id}-${i}`} to={`/systems?category=${cat.id}`} className="group shrink-0">
              <div className="bg-white shadow-sm border border-slate-100 hover:border-blue-200 px-5 py-3 md:px-8 md:py-4 rounded-xl md:rounded-2xl transition-all flex items-center gap-2 md:gap-3 select-none">
                <span className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors whitespace-nowrap text-sm md:text-lg">{cat.name_he}</span>
                <ArrowUpLeft className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );

  const renderHowItWorks = () => showHowItWorks && (
    <section key="how_it_works" className="py-20 lg:py-32 border-b border-slate-100">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        <div className="text-center mb-16 lg:mb-24">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-5">פשוט, חכם, יעיל.</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            הדרך הקלה להציג את המערכת שלך, להגיע לקהל רלוונטי, ולהתחיל לקבל חשיפה דרך פלטפורמה מקצועית ומסודרת.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 md:gap-12 lg:gap-8 relative">
          <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-slate-200 to-transparent opacity-50 -z-10" />
          
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 md:w-24 md:h-24 bg-white rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-3 md:mb-6 shadow-sm border border-slate-100">
              <UserPlus className="w-6 h-6 md:w-10 md:h-10 text-slate-900" />
            </div>
            <h3 className="text-xs md:text-xl font-bold text-slate-900 mb-1 md:mb-3">1. נרשמים בקלות</h3>
            <p className="text-[10px] md:text-base text-slate-500 leading-tight md:leading-relaxed max-w-xs font-medium px-1">יוצרים חשבון משתמש בחינם בפחות מדקה.</p>
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 md:w-24 md:h-24 bg-white rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-3 md:mb-6 shadow-sm border border-slate-100">
              <Layers className="w-6 h-6 md:w-10 md:h-10 text-slate-900" />
            </div>
            <h3 className="text-xs md:text-xl font-bold text-slate-900 mb-1 md:mb-3">2. מפרסמים מערכת</h3>
            <p className="text-[10px] md:text-base text-slate-500 leading-tight md:leading-relaxed max-w-xs font-medium px-1">ממלאים טופס קצר עם תמונות ופרטי האפליקציה שלכם.</p>
          </div>

          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 md:w-24 md:h-24 bg-white rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-3 md:mb-6 shadow-sm border border-slate-100">
              <Share2 className="w-6 h-6 md:w-10 md:h-10 text-slate-900" />
            </div>
            <h3 className="text-xs md:text-xl font-bold text-slate-900 mb-1 md:mb-3">3. מקבלים חשיפה</h3>
            <p className="text-[10px] md:text-base text-slate-500 leading-tight md:leading-relaxed max-w-xs font-medium px-1">לאחר בדיקה ואישור, המערכת שלך מוצגת לקהל של ב־Feevho.</p>
          </div>

        </div>

        <div className="mt-16 text-center flex flex-col sm:flex-row justify-center items-center gap-4">
          {!session ? (
            <Link to="/register">
              <Button size="lg" className="h-14 px-10 w-full sm:w-auto rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-2">
                <UserPlus className="w-5 h-5" />
                הרשמה ופרסום בחינם
              </Button>
            </Link>
          ) : (
            <Link to="/dashboard/create">
              <Button size="lg" className="h-14 px-10 w-full sm:w-auto rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-2">
                <Plus className="w-5 h-5" />
                הוספת מערכת חדשה
              </Button>
            </Link>
          )}
          <Link to="/publisher-guide">
            <Button size="lg" variant="outline" className="h-14 px-10 w-full sm:w-auto rounded-full border-slate-200 bg-white text-slate-900 hover:bg-slate-50 font-bold text-base shadow-sm transition-all hover:scale-[1.02] active:scale-95 gap-2">
              למדריך המלא למפרסמים
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );

  const renderFeatured = () => showFeatured && featuredSystems && featuredSystems.length > 0 && (
    <section key="featured" className="py-24 lg:py-32 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">חדש על המדף</h2>
          <p className="text-slate-500 mt-4 text-lg lg:text-xl max-w-2xl mx-auto font-light">מערכות חדשות שנוצרו על ידי הקהילה ונבדקו על ידינו.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {featuredSystems.map((system: any) => (
            <SystemCard key={system.id} system={system} />
          ))}
        </div>
        <div className="mt-16 text-center">
           <Link to="/systems">
            <Button size="lg" className="h-14 px-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-2">
              לכל המערכות
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );

  const renderArticles = () => showArticles && latestArticles && latestArticles.length > 0 && (
    <section key="articles" className="py-16 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">מאמרים</h2>
          <p className="text-slate-500 mt-3 text-base max-w-2xl mx-auto">עדכונים, מדריכים וחדשות מעולם ה-AI.</p>
        </div>

        <div className="flex overflow-x-auto pb-6 -mx-6 px-6 gap-4 snap-x snap-mandatory no-scrollbar md:grid md:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0 md:mx-0 md:px-0">
          {latestArticles.map((article: any) => (
            <Link key={article.id} to={`/articles/${article.slug}`} className="group block h-full w-[75vw] min-w-[280px] sm:w-[350px] md:w-auto md:min-w-0 snap-center">
              <div className="h-full border border-slate-100 rounded-2xl overflow-hidden flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                  {article.cover_image ? (
                    <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50"><span className="text-4xl font-black opacity-10">BLOG</span></div>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                    <Calendar className="w-3 h-3" />
                    {new Date(article.created_at).toLocaleDateString('he-IL')}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight line-clamp-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 mb-6 flex-1">{article.excerpt}</p>
                  <div className="flex items-center text-blue-600 text-sm font-bold mt-auto">
                    קרא עוד <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 text-center">
           <Link to="/articles">
            <Button size="lg" className="h-14 px-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-2">
              לכל המאמרים
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );

  const renderFaq = () => showFaq && (
    <section key="faq" className="py-24 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
        <div className="flex flex-col items-center text-center gap-3 mb-12">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-slate-700 border border-slate-200 shadow-sm">
            <HelpCircle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">שאלות נפוצות</h2>
            <p className="text-base text-slate-500 mt-2">תשובות מהירות לשאלות הכי נפוצות.</p>
          </div>
        </div>

        <div className="space-y-3">
          {faqItems.map((item: any, idx: number) => (
            <div
              key={item.id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all hover:border-slate-300 shadow-sm"
            >
              <button
                onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                className="w-full flex items-center justify-between p-5 text-start"
              >
                <span className="font-medium text-slate-900 pe-4">{item.q}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${openFaq === item.id ? 'rotate-180' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${openFaq === item.id ? 'max-h-96' : 'max-h-0'}`}>
                <div className="px-5 pb-5 pt-0 border-t border-slate-50 mt-2">
                  <p className="text-slate-600 text-sm leading-relaxed mt-4">{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCta = () => showCta && (
    <section key="cta" className="py-32 border-b border-slate-100">
      <div className="container mx-auto px-6 lg:px-12 text-center">
        <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">מוכנים לגלות את הדבר הבא?</h2>
        <p className="text-slate-500 mt-4 text-lg max-w-md mx-auto">הצטרפו לקהילה צומחת של יוצרים ומשתמשים.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <Link to="/systems" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto gap-2 h-12 px-10 text-base font-medium rounded-xl bg-slate-900 hover:bg-slate-800 text-white shadow-none">
              <Search className="h-4 w-4" /> לקטלוג המערכות
            </Button>
          </Link>
          {!session && (
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 h-12 px-10 text-base font-medium rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-900 shadow-none">
                הצטרפות בחינם
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <Layout>
      <SEO 
        title="דף הבית" 
        description={heroDescription}
      />
      <div className="bg-white overflow-x-hidden">

        {/* HERO - APPLE STYLE (Always at the top) */}
        <section className="pt-20 lg:pt-32 pb-24 bg-white">
          <div className="container mx-auto px-6 lg:px-12">
            {!isLoadingSettings && showTopHeading && (
              <div className="text-center mb-12 lg:mb-20 animate-in fade-in zoom-in-95 duration-700">
                {topHeadingType === 'image' && topHeadingImage ? (
                   <img src={topHeadingImage} alt={APP_NAME} className="h-auto max-h-40 md:max-h-64 w-auto max-w-full mx-auto object-contain" />
                ) : (
                  <div className="relative inline-block">
                    <h2 dir="ltr" className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-blue-600 to-slate-900 pb-2 select-none opacity-90">
                      {topHeadingText}
                    </h2>
                  </div>
                )}
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              
              {/* Text side - Now order-1 (so it appears on the right in RTL) */}
              <div className="text-right space-y-10 order-1">
                <div className="space-y-6">
                  <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-slate-900 leading-[1.1] whitespace-pre-line">
                    {heroTitle}
                  </h1>
                  <p className="max-w-xl text-lg lg:text-xl text-slate-500 leading-relaxed font-normal ml-0 mr-auto whitespace-pre-line">
                    {heroDescription}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row-reverse justify-start gap-4 pt-4">
                  {session ? (
                    <Link to="/systems" className="w-full sm:w-auto">
                      <Button size="lg" className="w-full h-14 px-10 text-base font-bold rounded-2xl bg-[#1d1d1f] hover:bg-black text-white shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-3">
                        <Search className="h-5 w-5" />
                        גלו מערכות
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/register" className="w-full sm:w-auto">
                      <Button size="lg" className="w-full h-14 px-10 text-base font-bold rounded-2xl bg-[#1d1d1f] hover:bg-black text-white shadow-lg shadow-slate-200 transition-all hover:scale-[1.02] active:scale-95 gap-3">
                        <UserPlus className="h-5 w-5" />
                        הרשמה למערכת
                      </Button>
                    </Link>
                  )}
                  
                  <Link to="/publisher-guide" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="w-full h-14 px-10 text-base font-bold rounded-2xl border-slate-200 bg-white text-[#1d1d1f] hover:bg-slate-50 shadow-none transition-all gap-3">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      איך זה עובד למפרסמים?
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Media side - Now order-2 (so it appears on the left in RTL) */}
              <div className="relative order-2 aspect-[4/3] lg:aspect-square xl:aspect-[1.1/1] w-full group flex items-center justify-center">
                {isLoadingSettings ? (
                  <Skeleton className="w-full h-full rounded-[2.5rem]" />
                ) : (
                  <>
                    {mediaType === 'video' ? (
                      <video ref={heroVideoRef} src={heroMedia} className="w-full h-full object-cover rounded-[2.5rem] shadow-2xl shadow-slate-200/50" autoPlay muted loop playsInline preload="auto" />
                    ) : (
                      <img 
                        src={heroMedia} 
                        alt={`${APP_NAME} Platform`} 
                        className="w-full h-full object-contain border-none outline-none transition-transform duration-700 group-hover:scale-105 mix-blend-multiply [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]" 
                      />
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        </section>

        {/* Dynamically Rendered Sections based on DB order with Alternating Backgrounds via CSS wrapper */}
        <div className="[&>section:nth-child(odd)]:bg-slate-50/60 [&>section:nth-child(even)]:bg-white">
          {sectionsOrder.map(sectionKey => {
            switch(sectionKey) {
              case 'partners': return renderPartners();
              case 'about_us': return renderAboutUs();
              case 'stats': return renderStats();
              case 'categories': return renderCategories();
              case 'how_it_works': return renderHowItWorks();
              case 'featured': return renderFeatured();
              case 'articles': return renderArticles();
              case 'faq': return renderFaq();
              case 'cta': return renderCta();
              default: return null;
            }
          })}
        </div>

      </div>
      
      <PublisherPromoModal session={session} isEnabled={showPromoModal} />
    </Layout>
  );
};

export default Index;